/**
 * Shown when the caller has already submitted an annotation for this
 * record (either the first pass as the first annotator, or the second
 * pass as the second annotator). Prevents the same user from being
 * routed back into the labelling UI; the same-user rule is enforced
 * by the BE — this is the matching UX.
 */
import React from 'react';

import { Button } from '../../../../../ui/button';
import type { Annotation, DatasetRecord, GoLabDatasetRecordStatus } from '../../types';


/** One-line status copy explaining what's happening downstream of the
 *  user's completed pass. Keep short — the bigger story lives in the
 *  page header. */
const STATUS_LINES: Record<GoLabDatasetRecordStatus, string> = {
  pending: '',
  assigned_first: '',
  annotated_first: 'Awaiting a second annotator to complete the second pass.',
  assigned_second: 'Another annotator is currently working on the second pass.',
  annotated_both: 'Both passes are in — agreement check pending.',
  agreed: 'Both annotations agreed — the record is in the agreed pool.',
  disputed: 'The two annotations disagreed — this record is awaiting adjudication.',
  finalised: 'This record has been finalised.',
};


interface Props {
  record: DatasetRecord;
  myAnnotation: Annotation;
  onBack: () => void;
}


const AlreadyAnnotatedScreen: React.FC<Props> = ({ record, myAnnotation, onBack }) => {
  const passLabel =
    myAnnotation.annotationPass === 'first' ? 'first pass' : 'second pass';
  const submitted = new Date(myAnnotation.submittedAt).toLocaleString();
  const statusLine = STATUS_LINES[record.status];

  return (
    <div
      className="flex min-h-0 flex-col items-start justify-center gap-4 rounded-2xl border bg-background p-6"
      data-testid="already-annotated"
    >
      <div className="space-y-1">
        <h2 className="font-jakarta text-lg font-extrabold tracking-[-0.02em] text-foreground">
          You've already annotated this record.
        </h2>
        <p className="text-[13px] leading-[1.6] text-muted-foreground">
          You submitted the {passLabel} on {submitted}. {statusLine}
        </p>
      </div>
      <Button variant="outline" onClick={onBack}>
        ← Back to queue
      </Button>
    </div>
  );
};

export default AlreadyAnnotatedScreen;
